import { GUID } from '@common';
import { IDragData, SessionSummaryDragDataKey } from '@components';
import React, { useCallback, useState } from 'react';
import { AgendaItem } from '../../../api/SummeetClient';
import './AgendaItemDropComp.scss';

interface AgendaItemDropCompProps {
	onAgendaItemDrop: (agendaItem: AgendaItem, agendaItemIndex: number) => void,
	onAgendaItemReorderDrop: (oldIdex: number, newIndex: number) => void,
	newIndex: number,
	canEdit: boolean,
}

function AgendaItemDropComp(props: AgendaItemDropCompProps): JSX.Element {
	const {
		onAgendaItemReorderDrop,
		onAgendaItemDrop,
		newIndex,
		canEdit,
	} = props;

	const 
		[isOver, setIsOver] = useState(false),
		[dragOverTarget, setDragOverTarget] = useState(null);

	const onDragEnter = (ev) => {
		if (!canEdit) {
			return;
		}
		const jsonText = sessionStorage.getItem(SessionSummaryDragDataKey);
		if (jsonText) {
			const data = JSON.parse(jsonText) as IDragData;

			if (data.source == 'transcript') {
				setDragOverTarget(ev.target);
				ev.preventDefault();
				ev.dataTransfer.dropEffect = "copy";
				setIsOver(true);
			}

			if (data.source == 'agenda') {
				// Sync onDragOver and onDragEnter
				if (newIndex == data.agendaItemIndex || newIndex == data.agendaItemIndex + 1 ) {
					return;
				}

				ev.preventDefault();
				setDragOverTarget(ev.target);
				ev.dataTransfer.dropEffect = "move";
				setIsOver(true);
			}
		}
	}

	const onDragOver = (ev) => {
		if (!canEdit) {
			return;
		}
		const jsonText = sessionStorage.getItem(SessionSummaryDragDataKey);
		if (jsonText) {
			const data = JSON.parse(jsonText) as IDragData;
				
			if (data.source == 'agenda') {
				// Sync onDragOver and onDragEnter
				if ((newIndex == data.agendaItemIndex || newIndex == data.agendaItemIndex + 1 )
				) {
					return;
				}

				ev.preventDefault();
			} else {
				ev.preventDefault();
			}			
		}
	}

	const onDragLeave = (ev) => {
		if (!canEdit) {
			return;
		}

		if (ev.target != dragOverTarget) {
			return;
		}
		setIsOver(false);
		setDragOverTarget(null);
	}

	const onDrop = useCallback((ev) => {
		if (!canEdit) {
			return;
		}
		
		ev.preventDefault();
		setIsOver(false);
		setDragOverTarget(null);

		const jsonText = sessionStorage.getItem(SessionSummaryDragDataKey);
		if (jsonText) {
			const data = JSON.parse(jsonText) as IDragData;

			if (data.source == 'transcript') {
				const newItem = new AgendaItem({
					id: GUID(),
					annotation: null,
					speakers: [],
					text: data.text,
				})

				onAgendaItemDrop(newItem, newIndex);
			}
			
			if (data.source == 'agenda') {
				onAgendaItemReorderDrop(data.agendaItemIndex, newIndex);
			}
		}

		sessionStorage.setItem(SessionSummaryDragDataKey, null);

	}, [onAgendaItemReorderDrop, onAgendaItemDrop, newIndex, canEdit]);

	let classNames = "agenda-item-dropblock"; 
	if (isOver) {
		classNames += " agenda-item-dropblock_over";
	}

	return (<div
			className={classNames}
			onDragEnter={onDragEnter}
			onDragLeave={onDragLeave}
			onDrop={onDrop}
			onDragOver={onDragOver}
			>
			<div className="agenda-item-dropblock__content"></div>
		</div>);
}

const MemoisedAgendaItemDropComp = React.memo(AgendaItemDropComp);

export {MemoisedAgendaItemDropComp as AgendaItemDropComp};